import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InboxRoundedIcon from '@mui/icons-material/InboxRounded'
import API from '../../../services/API'
import PagePaper from '../../../components/mainFrame/PagePaper'
import PageHeader from '../../../components/PageHeader/PageHeader'
import {
  NotificationRow,
  getWebPath,
} from '../../../components/mainFrame/header/notificationRendering'

// Full notification history - the bell popover shows only the latest 30.
// Rows render identically to the popover (notificationRendering.jsx).

const PAGE_SIZE = 25

const NotificationsPage = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const load = useCallback((toPage) => {
    setLoading(true)
    API.get('/account/notifications', {
      params: { limit: PAGE_SIZE, skip: (toPage - 1) * PAGE_SIZE },
    })
      .then((res) => {
        if (res.status === 200) {
          setNotifications(res.data?.notifications ?? [])
          setUnreadCount(res.data?.unreadCount ?? 0)
          setTotal(res.data?.total ?? 0)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load(page)
  }, [load, page])

  const markRead = (ids) => {
    const now = new Date().toISOString()
    setNotifications((prev) =>
      prev.map((n) =>
        !ids || ids.includes(n._id) ? { ...n, readAt: n.readAt ?? now } : n,
      ),
    )
    setUnreadCount((prev) => (ids ? Math.max(prev - ids.length, 0) : 0))
    API.post('/account/notifications/mark-read', ids ? { ids } : {}).catch(
      () => {},
    )
  }

  const handleClick = (notification) => {
    if (!notification.readAt) markRead([notification._id])
    const path = getWebPath(notification)
    if (path) navigate(path)
  }

  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1)

  return (
    <PagePaper>
      <PageHeader
        actions={
          <Button
            size="small"
            variant="outlined"
            color="contrast"
            disabled={!unreadCount}
            onClick={() => markRead(null)}
          >
            Mark all read
          </Button>
        }
      />
      <Typography variant="caption" color="text.secondary">
        {unreadCount > 0
          ? `${unreadCount} unread of ${total}`
          : `${total} notification${total === 1 ? '' : 's'}`}
      </Typography>

      <Box mt={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : notifications.length === 0 ? (
          <Stack alignItems="center" gap={0.75} sx={{ py: 6, px: 2 }}>
            <InboxRoundedIcon color="disabled" />
            <Typography variant="body2" fontWeight={700}>
              You&apos;re all caught up
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Shift updates, approvals and alerts for you will appear here.
            </Typography>
          </Stack>
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0 }}>
            {notifications.map((notification) => (
              <NotificationRow
                key={notification._id}
                notification={notification}
                onClick={handleClick}
              />
            ))}
          </Box>
        )}
      </Box>

      {pageCount > 1 && (
        <Stack alignItems="center" mt={2}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(e, value) => setPage(value)}
            size="small"
          />
        </Stack>
      )}
    </PagePaper>
  )
}

export default NotificationsPage
